/* eslint-disable no-restricted-imports */
import React, {useEffect, useMemo, useRef} from 'react'
import {UIGameActionButton} from '../../../../common/UIGameActionButton'
import {
    UIGameResourceCardsTooltipContent,
    UIGameResourceCardsTooltipContentHandle,
} from '../../../../common/UIGameResourceCardsTooltipContent'
import {useActionButtonCommonErrors} from '../../utils'
import {KNIGHT_LEVEL1_INACTIVE_IMAGE_PATH} from './utils'
import {Tooltip} from '../../../../common/Tooltip'
import {T} from '../../../../common/T'
import {getCardsForPiece} from '@colonist/game-card-types'
import {ActionStatus, PieceTypes} from '@colonist/game-base-types'
import {useGameStore} from '../../../../store/GameStoreContext'
import {usePlayerValidators} from '../../../../store/selectors/usePlayerValidators'
import {ActionState} from '@colonist/game-states'
import {MapValidator, MechanicValidatorKnightPlayer} from '@colonist/game-mechanic-validators'
import {TranslationObject} from '@colonist/i18n'
import {useUIGameSocketContext} from '../../../../contexts/UIGameSocketContext'
import {useCurrentStateValidator} from '../../../../store/selectors/useCurrentStateValidator'

const cardToBuildKnight = getCardsForPiece(PieceTypes.Knight)

export const UIGameActionButtonKnightPlacement = ({
    className,
    onDisabledChange,
}: {
    onDisabledChange: (isDisabled: boolean) => void
    className?: string
}) => {
    const myColor = useGameStore((state) => state.myColor)
    const mechanicKnightState = useGameStore((state) => state.gameState.mechanicKnightState)
    const mapState = useGameStore((state) => state.gameState.mapState)
    const currentStateValidator = useCurrentStateValidator()
    const {getPlayerValidator, playerValidators} = usePlayerValidators()
    const playerValidator = getPlayerValidator(myColor)
    const tooltipRef = useRef<UIGameResourceCardsTooltipContentHandle>(null)
    const {errorHelperText, hasCommonErrors} = useActionButtonCommonErrors()
    const {sendPlaceKnight, sendCancelAction, sendClearCornerHighlight} = useUIGameSocketContext()
    const {actionStatus, level1KnightsThatCanBePlacedCount}: {
        actionStatus: ActionStatus
        level1KnightsThatCanBePlacedCount: number
    } = useMemo(() => {
        const knightPlayerState = mechanicKnightState?.[myColor]

        if(!knightPlayerState) {
            return {actionStatus: {canTakeAction: false}, level1KnightsThatCanBePlacedCount: 0}
        }
        const knightPlayerValidator = new MechanicValidatorKnightPlayer(playerValidator,
            new MapValidator(mapState), knightPlayerState)
        const useResources = true
        const canPlaceKnightActionStatus = knightPlayerValidator.canPlayerPlaceNewKnight(useResources)
        const level1KnightsThatCanBePlaced = knightPlayerValidator.getAmountOfKnightsOfLevelThatCanBePlacedOnMap(1)
        return {
            actionStatus: canPlaceKnightActionStatus,
            level1KnightsThatCanBePlacedCount: level1KnightsThatCanBePlaced}
    }, [playerValidators, mapState, mechanicKnightState, myColor])

    const triggerFlickerCardsAnimation = () => {
        const notEnoughResources = !playerValidator.hasResourceCardsInHand(cardToBuildKnight)
        if(notEnoughResources) {
            tooltipRef.current?.triggerFlickerAnimation()
        }
    }

    const disabled = hasCommonErrors || !actionStatus.canTakeAction
    const actionState = currentStateValidator.getPlayerActionState(myColor)
    const isPlacingKnight = actionState === ActionState.PlaceKnight && !disabled
    const icon = KNIGHT_LEVEL1_INACTIVE_IMAGE_PATH[myColor]
    const helperText: TranslationObject | undefined = errorHelperText || actionStatus.errorMessageIfCantTakeAction

    useEffect(() => {
        onDisabledChange(disabled)
    }, [disabled])

    const onClick = () => {
        if(disabled) return
        if(isPlacingKnight) {
            sendClearCornerHighlight()
            sendCancelAction()
        } else {
            sendPlaceKnight()
        }
    }

    return <Tooltip
        position='bottom'
        title={<T id='strings:game.tooltips.purchaseKnight'/>}>
        <UIGameActionButton
            className={className}
            count={level1KnightsThatCanBePlacedCount}
            countColor={myColor}
            disabled={disabled}
            helperText={helperText}
            highlight={isPlacingKnight}
            iconPath={icon}
            onClickActionIfDisabled={triggerFlickerCardsAnimation}
            tooltipCustomMenuComponent={
                <UIGameResourceCardsTooltipContent
                    cardInventory={playerValidator.resourceCards.cards}
                    cardsToBuild={cardToBuildKnight}
                    ref={tooltipRef}/>
            }
            onClick={onClick}
        />
    </Tooltip>
}
